import axios from "axios";
import { Dir, PORT } from "../Variables";

// Where the API lives, decided by how this bundle was BUILT — not by the
// hostname it happens to be served from.
//
// Keying this off `window.location.hostname` looked equivalent and is not: a
// production build opened on localhost (a VPS smoke test, a container, a
// tunnel pointed at the deployed port) took the development branch and asked
// for http://localhost:4000, which in production is nothing at all. Every
// request failed CORS against an origin that does not exist.
//
//   REACT_APP_API_ORIGIN set  → use it verbatim (split deployments, staging)
//   production build          → same origin; Express serves the SPA and /api
//                               together, so no CORS is involved at all
//   development               → the dev server on :3000 talking to :4000
const ORIGIN_OVERRIDE = (process.env.REACT_APP_API_ORIGIN || "").replace(/\/+$/, "");
const SAME_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "";

export const API_ORIGIN =
  ORIGIN_OVERRIDE ||
  (process.env.NODE_ENV === "production" ? SAME_ORIGIN : `http://${Dir}:${PORT}`);
export const API_BASE   = `${API_ORIGIN}/api`;

const api = axios.create({
    baseURL: API_BASE,
    // ngrok Free serves an HTML interstitial on first request to a tunnel
    // unless this header is present — without it, axios receives the
    // warning page instead of JSON. Value is arbitrary; ngrok only checks
    // for the header's existence.
    headers: { "ngrok-skip-browser-warning": "1" },
});

// =========================================================
// Burst control
//
// A dashboard screen legitimately fires 15-20 calls at once — the accounting
// overview alone requests five analytics endpoints in parallel. Browsers are
// happy to open all of them, and Hostinger's CDN edge reads the spike as
// automated abuse and answers 403 for several minutes, to the whole site.
//
// Capping in-flight requests keeps the same work happening a few milliseconds
// apart instead of simultaneously. Nothing is dropped and no caller changes;
// requests past the cap wait for a slot.
// =========================================================
const MAX_IN_FLIGHT = 4;
let inFlight = 0;
const waiting = [];

const acquireSlot = () => {
    if (inFlight < MAX_IN_FLIGHT) { inFlight++; return Promise.resolve(); }
    return new Promise((resolve) => waiting.push(resolve));
};
const releaseSlot = () => {
    const next = waiting.shift();
    if (next) next();          // hand the slot straight over
    else inFlight--;
};

// Attach JWT to every request.
api.interceptors.request.use(async (config) => {
    await acquireSlot();
    config.__slotHeld = true;
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Belt-and-suspenders: ensure the ngrok bypass header is set even
    // if a caller passed a custom `headers` object that dropped defaults.
    config.headers["ngrok-skip-browser-warning"] = "1";
    return config;
});

// Read the cached user (set at login) — used to exempt the Super Admin
// from the maintenance redirect.
const cachedUser = () => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
};

// Is this a 403 from the CDN edge rather than from our own API?
//
// The distinction matters: the app returns 403 as a real answer when a user
// lacks a permission, and retrying that would be pointless and confusing.
// Our API always replies JSON; the edge block is an HTML error page. So the
// content type, not the status, is what separates "you may not do this" from
// "the edge refused to pass this along".
const isEdgeBlock = (error) => {
    const res = error.response;
    if (!res || res.status !== 403) return false;
    const type = String(res.headers?.["content-type"] || "");
    if (type.includes("application/json")) return false;
    return type.includes("text/html") || typeof res.data === "string";
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
    (response) => { if (response.config?.__slotHeld) releaseSlot(); return response; },
    async (error) => {
        if (error.config?.__slotHeld) { releaseSlot(); error.config.__slotHeld = false; }

        // Edge throttling is transient — back off and try again rather than
        // surfacing a failure the user can do nothing about.
        if (isEdgeBlock(error)) {
            const cfg = error.config || {};
            cfg.__edgeRetries = (cfg.__edgeRetries || 0) + 1;
            if (cfg.__edgeRetries <= 3) {
                const wait = 1500 * 2 ** (cfg.__edgeRetries - 1)   // 1.5s, 3s, 6s
                           + Math.random() * 500;                   // jitter, so a
                await sleep(wait);                                  // burst does not
                return api(cfg);                                    // retry in lockstep
            }
        }

        const status = error.response?.status;

        // 401 (expired/invalid token) → clear auth state and bounce to /auth.
        //
        // Except inside a super admin's "login as" session: its token lasts an
        // hour, and when it runs out the admin's own session is still saved
        // in sessionStorage. Go back to it instead of stranding the admin on
        // the login screen with their real token left behind.
        if (status === 401 && window.location.pathname !== "/auth") {
            let back = null;
            try { back = JSON.parse(sessionStorage.getItem("ax_admin_return") || "null"); } catch { back = null; }
            if (back?.token) {
                sessionStorage.removeItem("ax_admin_return");
                localStorage.setItem("token", back.token);
                localStorage.setItem("user", JSON.stringify(back.user || {}));
                window.location.assign(back.companyId ? `/admin/companies/${back.companyId}` : "/admin/companies");
            } else {
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                window.location.assign("/auth");
            }
        }

        // 503 maintenance → company / merchant users are sent to the
        // maintenance screen, remembering where they were so «إعادة المحاولة»
        // brings them back there (not to /company, which a merchant cannot
        // open). The Super Admin is exempt — the backend never blocks
        // /api/super-admin — and so are the public pages, which the gate
        // does not block either.
        if (
            status === 503 &&
            error.response?.data?.maintenance &&
            cachedUser()?.role !== "admin" &&
            window.location.pathname !== "/maintenance"
        ) {
            const d = error.response.data;
            try {
                sessionStorage.setItem("ax_maintenance", JSON.stringify({
                    title: d.title || "",
                    message: d.message || "",
                    estimatedEnd: d.estimatedEnd || "",
                    from: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                }));
            } catch { /* ignore storage failure */ }
            window.location.assign("/maintenance");
        }

        return Promise.reject(error);
    }
);

// Convert a relative server path (e.g. "Assets/images/stock/123.png")
// into a fully-qualified URL the browser can load.
export const assetUrl = (relativePath) =>
    relativePath ? `${API_ORIGIN}/${relativePath.replace(/^\/+/, "")}` : "";

export default api;
